<template>
	<div class="m-line-bar">
		<div v-if="showChart" class="chart-main">
			<div class="chart-canvas" ref="chartRef"></div>
			<div v-if="legends.length" class="legend-container">
				<div class="legend-wrapper" v-for="(item, index) in legends" :key="index">
					<span class="legend-item" :style="{ background: colors[index] }"></span
					><span class="legend-word">{{ item }}</span>
				</div>
			</div>
		</div>
		<Empty v-else class="chart-empty"></Empty>
	</div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import * as echarts from 'echarts/core';
import { LineChart, BarChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import {
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent
} from 'echarts/components';
import merge from 'lodash.merge';
import { convertNumberToStr } from '@/utils/api';
import { ECHARTS_COLORS } from '@/utils/enum';
import { calcNiceTicks } from '@/utils/scale';
import { useCommonData } from '@/hooks';

echarts.use([
	LineChart,
	BarChart,
	GridComponent,
	SingleAxisComponent,
	ToolboxComponent,
	TooltipComponent,
	AxisPointerComponent,
	BrushComponent,
	TitleComponent,
	LegendComponent,
	DataZoomComponent,
	CanvasRenderer
]);

const props = defineProps({
	title: {
		type: String,
		default: ''
	},
	xAxisData: {
		type: Array
	},
	seriesData: {
		type: Array,
		required: true,
		default: () => []
	},
	legends: {
		type: Array,
		default: () => [] // ['name']
	},
	chartOptions: {
		type: Object,
		default: () => ({})
	},
	isReverse: {
		type: Boolean,
		default: false
	},
	tooltipLabel: {
		type: String,
		default: ''
	},
	timeFormatter: {
		type: Function,
		default: value => value
	},
	customStyle: {
		type: String,
		default: ''
	},
	yLeftAxisLabelFormatter: {
		type: Function,
		default: value => {
			return convertNumberToStr(value);
		}
	}
});

const emits = defineEmits(['finished']);

const chart = ref(null);
const chartRef = ref(null);

const { getUpperThemeColor } = useCommonData();
const colors = computed(() => ECHARTS_COLORS[getUpperThemeColor.value]);

const showChart = computed(() => {
	return props.seriesData.some(series => {
		return series.data?.some(ele => {
			let value;
			if (Array.isArray(ele)) {
				value = ele[1];
			} else if (typeof ele === 'object') {
				value = ele.value;
			} else {
				value = ele;
			}
			return !['', '-', undefined, null, NaN].includes(value);
		});
	});
});

watch(showChart, newVal => {
	if (!newVal) {
		chart.value?.dispatchAction({
			type: 'hideTip'
		});
		chart.value?.clear();
		chart.value?.dispose();
		chart.value = null;
	}
});

onMounted(() => {
	if (showChart.value) {
		initChart();
	}
});

onUnmounted(() => {
	chart.value?.dispatchAction({
		type: 'hideTip'
	});
	chart.value?.clear();
	chart.value?.dispose();
	chart.value = null;
});

// 考虑负数情况，将数据转换成带有起始和结束位置的堆叠数据
function computeStackedData(data) {
	const cols = data[0].length;
	const rows = data.length;
	const stackedData = Array(rows)
		.fill(0)
		.map(() => new Array(cols).fill(0));
	const columnSums = new Array(cols).fill(0);

	for (let row = 0; row < rows; row++) {
		for (let col = 0; col < cols; col++) {
			const value = data[row][col];
			// 起始位置为当前列的累加和
			const start = columnSums[col];
			// 结束位置为起始位置加上当前值（可正可负）
			const end = start + value;
			// 将起始、结束和原始值存入结果数组
			stackedData[row][col] = { start, end, value };
			// 更新该列的累加和，为下一个数据点的起始位置做准备
			columnSums[col] = end;
		}
	}

	return stackedData;
}
// 确定 Y 轴范围
const getMinMax = stackedData => {
	const flatData = stackedData.flat();
	const starts = flatData.map(d => d.start);
	const ends = flatData.map(d => d.end);
	const values = flatData.map(d => d.value);

	// 合并所有可能影响Y轴范围的值（起始、结束和原始值）
	const allValues = [...starts, ...ends, ...values];
	const min = Math.min(...allValues);
	const max = Math.max(...allValues);

	return { min, max };
};

const initChart = () => {
	if (!chartRef.value) return;

	chart.value?.clear();
	chart.value?.dispose();
	chart.value = echarts.init(chartRef.value);

	let xAxisData = props.xAxisData;
	if (!xAxisData) {
		xAxisData = props.seriesData[0].data.map(ele => {
			if (Array.isArray(ele)) {
				return ele[0];
			} else {
				return ele.name;
			}
		});
	}

	const yAxisData = props.seriesData
		.filter(series => series.yAxis !== 1)
		.map(series =>
			series.data.map(ele => {
				if (Array.isArray(ele)) {
					return ele[1];
				} else {
					return ele.value;
				}
			})
		)
		.filter(val => !['', '-', undefined, null, NaN].includes(val));

	const stackedData = computeStackedData(yAxisData);
	const { min, max } = getMinMax(stackedData);
	const ticks = calcNiceTicks([min, max]);

	const options = {
		grid: {
			top: 32,
			left: 0,
			right: 0,
			bottom: 0,
			containLabel: true
		},
		color: colors.value,
		animation: false,
		legend: {
			top: 0,
			padding: 0,
			itemGap: 12,
			itemWidth: 6,
			itemHeight: 6,
			inactiveColor: 'C9CDD4',
			textStyle: {
				color: '#4E5969',
				fontSize: 12,
				lineHeight: 18,
				padding: [0, 0, 0, 2],
				height: 18
			},
			icon: 'roundRect',
			selectedMode: false
		},
		tooltip: {
			trigger: 'axis',
			confine: true,
			extraCssText: 'padding: 4px 6px;border: 0;border-radius: 1px;z-index: 1;',
			axisPointer: {
				type: 'line',
				snap: true,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'dashed'
				},
				handle: {
					show: false
				}
			},
			formatter: params => {
				if (props.isReverse) {
					params.reverse();
				}
				return tooltipFormatter(
					params
						.map(item => {
							let deepValue;
							if (openCustomStyle.value) {
								deepValue = item.data.value; // 取值结构兼容
							} else {
								deepValue = item.data;
							}
							let value, unit;
							if (Array.isArray(deepValue)) {
								value = deepValue[1];
								unit = deepValue[2];
							} else if (typeof deepValue === 'object') {
								value = deepValue.value;
								unit = deepValue.unit;
							} else {
								value = deepValue;
							}
							return {
								color: item.color,
								seriesName: item.seriesName,
								value:
									value != null
										? unit === '%'
											? value.toFixed(2)
											: convertNumberToStr(value)
										: null,
								unit
							};
						})
						.filter(ele => ele.value != null)
				);
			}
		},
		xAxis: {
			type: 'category',
			axisLine: {
				show: true,
				onZero: false,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'solid'
				}
			},
			axisTick: {
				alignWithLabel: true,
				length: 4,
				lineStyle: {
					color: '#E8EAEF',
					width: 1,
					type: 'solid'
				}
			},
			axisLabel: {
				margin: 8,
				color: '#989898',
				fontSize: 12,
				align: 'center',
				lineHeight: 16,
				formatter: function (value) {
					return props.timeFormatter?.(value) || value;
				}
			},
			splitLine: {
				show: false
			},
			data: xAxisData
		},
		yAxis: [
			{
				type: 'value',
				axisLine: {
					show: false
				},
				axisTick: {
					show: false
				},
				axisLabel: {
					margin: 8,
					color: '#989898',
					fontSize: 12,
					lineHeight: 16,
					formatter: props.yLeftAxisLabelFormatter
				},
				splitLine: {
					lineStyle: {
						color: '#E5E6EB',
						width: 1,
						type: 'dashed'
					}
				},
				splitNumber: 5,
				...ticks
			},
			{
				type: 'value',
				show: false,
				axisLabel: {
					show: false,
					width: 0,
					margin: 0
				},
				splitNumber: 5
			}
		],
		series: props.seriesData.map(series => {
			if (series.type === 'bar') {
				return merge(
					{
						barMaxWidth: 8,
						barCategoryGap: 4,
						large: true
					},
					preformatSeries(series)
				);
			} else if (series.type === 'line') {
				return merge(
					{
						type: 'line',
						symbol: 'emptyCircle',
						symbolSize: 6,
						showSymbol: true,
						lineStyle: {
							width: series.width || 1
						}
					},
					series
				);
			}
		})
	};

	chart.value.on('finished', () => {
		chart.value?.off('finished');
		chart.value?.dispatchAction({
			type: 'hideTip'
		});
		emits('finished', chart.value);
	});

	chart.value.setOption(merge(options, props.chartOptions));

	const _series = options.series;
	lastActiveUpdate(_series);

	// zrender 事件与 echarts 事件不同。前者是当鼠标在任何地方都会被触发，而后者是只有当鼠标在图形元素上时才能被触发。
	chart.value.getZr().on('click', event => {
		const pos = [event.offsetX, event.offsetY];
		// 检查点击位置是否在图表的网格区域内
		if (chart.value.containPixel('grid', pos)) {
			// 将像素坐标转换为数据索引
			let [dataIndex] = chart.value.convertFromPixel({ seriesIndex: 0 }, pos);
			const newSeries = onChartChecked(dataIndex, _series);
			// 重新更新图表数据
			newSeries &&
				chart.value.setOption({
					series: newSeries
				});
		}
	});

	//点击非图表区域隐藏掉tooltip
	document.addEventListener('touchstart', event => {
		const chartContainer = chartRef.value;
		// 判断点击目标是否在图表容器外
		if (!chartContainer?.contains(event.target)) {
			chart.value?.dispatchAction({ type: 'hideTip' }); // 隐藏所有 Tooltip
		}
	});
};

const tooltipFormatter = params => {
	return `
    <div class='line-bar-tooltip'>
      ${params
			.map(
				item => `<div class='line-wrapper'>
              <div class="color-name">
                <span class='item-color' style="background: ${item.color}"></span>
                <p class='item-name'>${item.seriesName}：</p>
              </div>
              <span class='item-value'>${item.value}${item.unit || ''}</span>
            </div>`
			)
			.join('')}
    </div>
  `;
};

const openCustomStyle = computed(() => ['fund_holding'].includes(props.customStyle));
const activeIndex = ref(-1);

const lastActiveUpdate = series => {
	if (!openCustomStyle.value) return;
	if (series?.[0].data.length) {
		activeIndex.value = series[0].data.length - 1;
	}
};
const preformatSeries = seriesItem => {
	if (!openCustomStyle.value) return seriesItem;

	const { data, ...rest } = seriesItem;
	return {
		...rest,
		data: data.map((val, index) => {
			return {
				value: val,
				itemStyle: {
					// 默认选中最后（最新）一根柱子
					opacity: index === data.length - 1 ? 1 : 0.4
				}
			};
		})
	};
};
const onChartChecked = (idx, _series) => {
	if (!openCustomStyle.value) return;
	if (typeof idx !== 'number') return;

	const dataIndex = Math.abs(idx);
	const sameClickedIndex = activeIndex.value === dataIndex;
	const clickedIndex = dataIndex;
	activeIndex.value = clickedIndex;

	let pickValue;
	const newSeries = _series.map(series => {
		const { data, ...rest } = series;
		return {
			...rest,
			data: data.map((item, index) => {
				const deepValue = item.value || item;
				if (index === clickedIndex) pickValue = deepValue;
				return {
					value: deepValue,
					itemStyle: {
						// 更新柱状图的透明度，以突出高亮选中
						opacity: index === clickedIndex ? 1 : 0.4
					}
				};
			})
		};
	});

	if (sameClickedIndex) {
		// emits('data-checked', { index: null, data: null });
		// console.log('-------- 反选');
		// activeIndex.value = -1; // reset
	} else {
		emits('data-checked', { index: clickedIndex, data: pickValue[0] });
		// console.log('-------- 正选', clickedIndex, pickValue);
	}

	return newSeries;
};

watch(
	() => props.seriesData,
	() => {
		if (showChart.value) {
			nextTick(() => {
				initChart();
			});
		}
	},
	{ immediate: true, deep: true }
);
</script>

<style lang="less" scoped>
.m-line-bar,
.chart-empty {
	width: 100%;
	height: 100%;

	.chart-main {
		height: 100%;
		display: flex;
		flex-direction: column;

		.chart-canvas {
			flex: 1;
			min-height: 0;
		}
		.legend-container {
			margin: 0 auto;
			margin-top: 16px;
			margin-bottom: -4px;
			width: 320px;

			display: flex;
			align-items: center;
			justify-content: center;
			flex-wrap: wrap;

			.legend-wrapper {
				margin: 0 5px 4px;
				display: flex;
				align-items: center;
			}
			.legend-item {
				margin: 3px;
				width: 10px;
				height: 10px;
				border-radius: 2px;
			}
			.legend-word {
				color: #4e5969;
				text-align: right;

				font-size: 10px;

				font-weight: 400;
				line-height: 16px; /* 123.077% */
				white-space: nowrap;
			}
		}
	}
}
</style>
<style lang="less">
.line-bar-tooltip {
	.item-color {
		margin-right: 4px;
		width: 6px;
		height: 6px;
		border-radius: 2px;
	}
	.item-name {
		max-width: 25vw;
		color: #989898;

		font-size: 11px;

		font-weight: 400;
		line-height: 15px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.item-value {
		margin-left: 10px;
		color: #333;

		font-size: 11px;

		font-weight: 400;
		line-height: 15px;
	}
	.line-wrapper {
		display: flex;
		align-items: center;
		justify-content: space-between;

		.color-name {
			display: flex;
			align-items: center;
		}
	}
	.line-wrapper + .line-wrapper {
		margin-top: 8px;
	}
}
</style>
